/**
 * A landing slot: the gap a dragged team would drop INTO.
 *
 * This mirrors how the legacy Setup tab's tree library worked. It never asked
 * "which row are you over" — it computed an insertion point between rows and
 * drew a landing pad there. That distinction is what makes the END of a
 * division reachable: the slot below a division's last team is a real position,
 * whereas a row-targeting model only ever offers the row itself or the next
 * division's header.
 */
export interface DropSlot {
  /** The row the slot is measured against. */
  overId: string
  /** True when the slot is BELOW that row rather than above it. */
  after: boolean
}

export interface SlotRow {
  id: string
  /** Row top edge, in the same coordinate space as the measured Y. */
  top: number
  /** Row bottom edge. */
  bottom: number
}

/**
 * The slot a given vertical position falls in.
 *
 * `y` is the DRAGGED ROW's centre, not the pointer — see `resolveDropTarget`
 * for why. Rows are stacked with no gaps, so any position inside the list maps
 * to exactly one slot: above a row's midpoint means "before it", below means
 * "after it".
 *
 * Returns null when `y` is outside every row. That is deliberate: the rows
 * handed in are one division's teams, and clamping to the first or last of them
 * would silently target a division the drag never reached. The caller decides
 * what an out-of-range drag means.
 *
 * `rows` must be in visual order.
 */
export const resolveDropSlot = (
  rows: SlotRow[],
  y: number
): DropSlot | null => {
  for (const row of rows) {
    if (y >= row.top && y < row.bottom) {
      const midpoint = row.top + (row.bottom - row.top) / 2
      return { overId: row.id, after: y >= midpoint }
    }
  }

  return null
}
